import React, { useContext, useState, useCallback } from "react";
import { getInitials } from "helpers/initials";
import { Button, Input, message, Modal } from "antd";
import { AuthContext } from "app/app-context";
import { formatDate } from "helpers/date-time";
import "./ProfileDetail.scss";

function ProfileDetail() {
  const [appUserInfo] = useContext(AuthContext);
  const [isChangePasswordVisible, setIsChangePasswordVisible] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleOpenChangePassword = useCallback(() => {
    setIsChangePasswordVisible(true);
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
  }, []);

  const handleCloseChangePassword = useCallback(() => {
    setIsChangePasswordVisible(false);
  }, []);

  const handleChangePassword = useCallback(() => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      message.error("Vui lòng nhập đầy đủ thông tin");
      return;
    }
    if (newPassword !== confirmPassword) {
      message.error("Mật khẩu mới không khớp");
      return;
    }
    if (newPassword.length < 6) {
      message.error("Mật khẩu phải có ít nhất 6 ký tự");
      return;
    }

    message.info("Tính năng đang phát triển");
    setIsChangePasswordVisible(false);
  }, [oldPassword, newPassword, confirmPassword]);


  const roles = (appUserInfo?.appUserRoleMappings ?? [])
    .map((mapping) => mapping.role?.name)
    .filter(Boolean) as string[];

  // Một ô nhãn + giá trị; trống thì hiện "—" màu nhạt để mắt lướt qua.
  const row = (label: string, value?: React.ReactNode) => (
    <div className="profile-detail__row">
      <dt>{label}</dt>
      <dd className={value ? undefined : "profile-detail__empty"}>{value || "—"}</dd>
    </div>
  );

  return (
    <div className="profile-detail">
      {/* Đầu trang: avatar, tên, vai trò; việc duy nhất của trang (đổi mật khẩu) đứng bên phải. */}
      <div className="profile-detail__head">
        <div className="profile-detail__identity">
          {/* Cùng kiểu avatar với header để hai chỗ nhìn ra một người. */}
          {appUserInfo?.avatar ? (
            <img className="profile-detail__avatar" src={appUserInfo.avatar} alt="" />
          ) : (
            <span className="profile-detail__avatar profile-detail__avatar--initials">
              {getInitials(appUserInfo?.displayName)}
            </span>
          )}
          <div className="profile-detail__identity-text">
            <h1 className="profile-detail__name">{appUserInfo?.displayName || "—"}</h1>
            <div className="profile-detail__roles">
              {roles.length > 0 ? (
                roles.map((role) => (
                  <span className="profile-detail__role" key={role}>
                    {role}
                  </span>
                ))
              ) : (
                <span className="profile-detail__muted">Chưa có vai trò</span>
              )}
            </div>
          </div>
        </div>
        <button type="button" className="profile-detail__btn" onClick={handleOpenChangePassword}>
          Đổi mật khẩu
        </button>
      </div>

      <div className="profile-detail__grid">
        <section className="profile-detail__card">
          <div className="profile-detail__card-head">
            <h2 className="profile-detail__card-title">Thông tin cá nhân</h2>
          </div>
          <dl className="profile-detail__rows profile-detail__rows--two">
            {row("Họ và tên", appUserInfo?.displayName)}
            {row("Số điện thoại", appUserInfo?.phoneNumber)}
            {row("Ngày sinh", appUserInfo?.birthday && formatDate(appUserInfo.birthday))}
            {row("Giới tính", appUserInfo?.sex?.name)}
            {row("Chức vụ", appUserInfo?.position)}
            {row("Địa chỉ", appUserInfo?.address)}
          </dl>
        </section>

        <section className="profile-detail__card">
          <div className="profile-detail__card-head">
            <h2 className="profile-detail__card-title">Đăng nhập</h2>
          </div>
          <dl className="profile-detail__rows">
            {row("Tên đăng nhập", appUserInfo?.username)}
            {row("Email", appUserInfo?.email)}
          </dl>
        </section>
      </div>

      {/* Change Password Modal */}
      <Modal
        title="Đổi mật khẩu"
        className="profile-detail__modal"
        visible={isChangePasswordVisible}
        onCancel={handleCloseChangePassword}
        footer={[
          <Button key="cancel" onClick={handleCloseChangePassword}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleChangePassword}
          >
            Xác nhận
          </Button>,
        ]}
      >
        <div className="profile-detail__password-form">
          <div className="profile-detail__form-group">
            <label htmlFor="profile-old-password">Mật khẩu hiện tại</label>
            <Input.Password
              id="profile-old-password"
              placeholder="Nhập mật khẩu hiện tại"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
            />
          </div>
          <div className="profile-detail__form-group">
            <label htmlFor="profile-new-password">Mật khẩu mới</label>
            <Input.Password
              id="profile-new-password"
              placeholder="Nhập mật khẩu mới"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div className="profile-detail__form-group">
            <label htmlFor="profile-confirm-password">Xác nhận mật khẩu mới</label>
            <Input.Password
              id="profile-confirm-password"
              placeholder="Nhập lại mật khẩu mới"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ProfileDetail;
